import React, { useState, useMemo } from 'react'
import { Link } from '@inertiajs/react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { currencySymbol as getCurrencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym } from '@/lib/format'
import { Shield, Scale, Zap, Ban, AlertTriangle, Users, TrendingUp, TrendingDown, ArrowRight, Minus } from 'lucide-react'
import type { SharedProps } from '@/types/shared'

interface MarketPositionProps extends SharedProps {
  sensitivity: string
  maxAdjustmentPct: number
  hasCompset: boolean
  compsetCount: number
}

// ── Illustrative constants for the "You vs. market" preview — mirror the shape of
// app/lib/app/rate_engine/stages/market_position_stage.rb. This is a WORKED EXAMPLE only:
// the controller does not pass a live compset median or rate index, so nothing here
// reads live rate data — it computes on constants (like PriceGuardrails' worked example).
// Backend is source of truth; the real stage runs against your actual compset median.
const PREVIEW_BASE_RATE = 100 // illustrative running rate the pipeline has built so far
const PREVIEW_RATE_INDEX = 0.82 // illustrative: you're priced at 82% of the compset median
const PREVIEW_MEDIAN = Math.round(PREVIEW_BASE_RATE / PREVIEW_RATE_INDEX) // ~£122 typical competitor

// Sensitivity scale + preset cap, matching the engine's presets.
const PREVIEW_PRESETS: Record<string, { scale: number; cap: number }> = {
  conservative: { scale: 0.6, cap: 8 },
  balanced: { scale: 1.0, cap: 12 },
  aggressive: { scale: 1.4, cap: 18 },
}

// rate_index → base move %. < 1.0 = cheaper than market (boost up), > 1.0 = pricier (soften down).
function previewBaseMovePct(rateIndex: number): number {
  if (rateIndex < 0.85) return 10
  if (rateIndex < 0.95) return 4
  if (rateIndex < 1.05) return 0
  if (rateIndex < 1.15) return -4
  return -10
}

const SENSITIVITY_OPTIONS = [
  {
    value: 'none',
    label: 'Disabled',
    icon: Ban,
    detail:
      'Market data does not affect pricing. Rates are set purely by your other pricing rules. Use this until your compset is configured and collecting data.',
    range: null,
  },
  {
    value: 'conservative',
    label: 'Conservative',
    icon: Shield,
    detail:
      'Small adjustments based on market position. Best for price-sensitive markets where large swings could lose bookings. Nudges rates gently when you are significantly over- or under-priced.',
    range: '\u00b18%',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    icon: Scale,
    detail:
      'Moderate adjustments that balance competitiveness with revenue. The recommended starting point for most properties. Responds meaningfully to market signals without overreacting.',
    range: '\u00b112%',
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    icon: Zap,
    detail:
      'Larger adjustments that closely track market movements. Best for properties in highly competitive areas where being priced wrong costs bookings quickly. Reacts strongly to competitor pricing.',
    range: '\u00b118%',
  },
]

export default function MarketPosition({
  currentUser,
  property,
  properties,
  sensitivity: initialSensitivity,
  maxAdjustmentPct: initialMaxAdjustmentPct,
  hasCompset,
  compsetCount,
}: MarketPositionProps) {
  const base = property.basePath
  const sym = getCurrencySymbol(property.currency)

  const [sensitivity, setSensitivity] = useState(initialSensitivity)
  const [maxAdjustmentPct, setMaxAdjustmentPct] = useState(initialMaxAdjustmentPct.toString())
  const [saving, setSaving] = useState(false)
  const review = usePricingReview()

  const isDirty = useMemo(() => {
    if (sensitivity !== initialSensitivity) return true
    if (maxAdjustmentPct !== initialMaxAdjustmentPct.toString()) return true
    return false
  }, [sensitivity, initialSensitivity, maxAdjustmentPct, initialMaxAdjustmentPct])

  // Worked-example "You vs. market" preview. Reacts to the sensitivity + max-cap the operator
  // is currently choosing, so they can see how their settings would move an underpriced rate —
  // BEFORE the competitor-rate feed is live. All arithmetic is on constants (never live rates).
  const preview = useMemo(() => {
    const baseMovePct = previewBaseMovePct(PREVIEW_RATE_INDEX)
    if (sensitivity === 'none') {
      return { disabled: true, finalPct: 0, adjusted: PREVIEW_BASE_RATE, capHit: false, effectiveCap: 0 }
    }
    const preset = PREVIEW_PRESETS[sensitivity] ?? PREVIEW_PRESETS.balanced
    const userCap = Number(maxAdjustmentPct) || 12
    // The tighter of the preset cap and the operator's max-adjustment cap wins.
    const effectiveCap = Math.min(preset.cap, userCap)
    const scaledPct = baseMovePct * preset.scale
    const cappedRaw = Math.max(-effectiveCap, Math.min(effectiveCap, scaledPct))
    // House rule: round the percentage to 1 decimal before it is applied.
    const finalPct = Math.round(cappedRaw * 10) / 10
    const adjusted = Math.round(PREVIEW_BASE_RATE * (1 + finalPct / 100))
    return {
      disabled: false,
      finalPct,
      adjusted,
      capHit: Math.abs(scaledPct) > effectiveCap + 1e-9,
      effectiveCap,
    }
  }, [sensitivity, maxAdjustmentPct])

  // Reviewed on the server first (change safety R1.5), then sent by the same PATCH.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      market_position_sensitivity: sensitivity,
      max_market_adjustment_pct: Number(maxAdjustmentPct) || 12.0,
    }
    review.open(`${base}/pricing-rules/market-position/review`, payload, () =>
      inertiaSave('patch', `${base}/pricing-rules/market-position`, payload, {
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      })
    )
  }

  return (
    <OperatorLayout title="Market Position" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="market-position"
          help={{
            content:
              "Market Position adjusts your rates based on how you're priced relative to your competitive set. When you're cheaper than the market, rates nudge up to capture more revenue. When you're expensive, rates soften to stay competitive.",
            learnMore: '/guides/market-position',
          }}
        />

        {/* Compset Status */}
        {!hasCompset ? (
          <div
            className="animate-slide-in rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-5 shadow-sm"
            style={{ animationDelay: '100ms' }}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  No competitors configured
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add competitors to your compset to enable market-based pricing. Market position
                  adjustments require competitor rate data to compare against.
                </p>
                <Link
                  href={`${base}/market-intelligence/compset`}
                  className="mt-3 inline-flex items-center text-xs font-medium text-primary hover:text-primary/80"
                >
                  Configure Compset →
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="animate-slide-in rounded-xl border border-border/60 border-l-4 border-l-primary bg-white p-5 shadow-sm"
            style={{ animationDelay: '100ms' }}
          >
            <div className="flex items-start gap-3">
              <Users className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {compsetCount} competitor{compsetCount !== 1 ? 's' : ''} in your compset
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Competitor rates are collected daily and used to calculate your market position.
                  The rate engine compares your prices against the compset median to determine
                  adjustments.
                </p>
                <Link
                  href={`${base}/market-intelligence/compset`}
                  className="mt-3 inline-flex items-center text-xs font-medium text-primary hover:text-primary/80"
                >
                  Manage Compset →
                </Link>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sensitivity Selector */}
          <div
            className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm"
            style={{ animationDelay: '150ms' }}
          >
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Market Position Sensitivity
            </h3>
            <p className="text-xs text-muted-foreground mb-5">
              How aggressively should rates adjust based on your competitive position? This controls
              how much your rates move when the rate engine detects you are priced above or below
              the compset median.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {SENSITIVITY_OPTIONS.map((opt) => {
                const Icon = opt.icon
                const isActive = sensitivity === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSensitivity(opt.value)}
                    className={cn(
                      'rounded-xl border p-4 text-left transition-all cursor-pointer',
                      isActive
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border/60 hover:border-border hover:bg-muted/30'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={cn(
                          'flex h-7 w-7 items-center justify-center rounded-lg',
                          isActive ? 'bg-primary/10' : 'bg-muted'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-3.5 w-3.5',
                            isActive ? 'text-primary' : 'text-muted-foreground'
                          )}
                        />
                      </div>
                      <p
                        className={cn(
                          'text-sm font-semibold',
                          isActive ? 'text-primary' : 'text-foreground'
                        )}
                      >
                        {opt.label}
                      </p>
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground">{opt.detail}</p>
                    {opt.range && (
                      <div className="mt-3 rounded-lg bg-muted/30 px-3 py-2 text-center">
                        <span className="text-xs font-semibold text-muted-foreground">
                          Typical range: {opt.range}
                        </span>
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Max Adjustment */}
          {sensitivity !== 'none' && (
            <div
              className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm"
              style={{ animationDelay: '200ms' }}
            >
              <h3 className="text-sm font-semibold text-foreground mb-1">
                Maximum Market Adjustment
              </h3>
              <p className="text-xs text-muted-foreground mb-4">
                The most that market position alone can move your rate, up or down. This caps the
                adjustment regardless of how far your rate is from the compset median. Other stages
                (demand, seasons, events) are not affected by this limit.
              </p>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    max="50"
                    className="w-24 text-center rounded-lg text-sm font-semibold pr-7"
                    value={maxAdjustmentPct}
                    onChange={(e) => setMaxAdjustmentPct(e.target.value)}
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    %
                  </span>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                At &plusmn;{Number(maxAdjustmentPct) || 12}%, a {formatMoneySym(100, sym)} base rate stays between{' '}
                {formatMoneySym(100 - (Number(maxAdjustmentPct) || 12), sym)} and{' '}
                {formatMoneySym(100 + (Number(maxAdjustmentPct) || 12), sym)} from market position alone
              </p>
            </div>
          )}

          {/* How it works */}
          <div
            className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm"
            style={{ animationDelay: '250ms' }}
          >
            <h3 className="text-sm font-semibold text-foreground mb-3">How Market Position Works</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  1
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground">Rate Index Calculation</p>
                  <p className="text-xs text-muted-foreground">
                    Your rate is divided by the compset median to produce a rate index. A value of
                    1.0 means you are priced at the market average.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  2
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground">Adjustment Direction</p>
                  <p className="text-xs text-muted-foreground">
                    Below 0.95x (underpriced): rates nudge up to capture revenue. Above 1.05x
                    (overpriced): rates soften to stay competitive. Between 0.95-1.05x: no
                    adjustment.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  3
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground">Supply Scarcity Boost</p>
                  <p className="text-xs text-muted-foreground">
                    When competitors are sold out, an extra boost is applied. Fewer available
                    alternatives means you can command higher rates.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* You vs. market — worked-example preview of the effect your current settings would have.
              Illustrative figures on a constant £100 rate priced below a ~£122 compset median;
              no live rate data is used (the feed that provides the real median is still rolling out). */}
          <div
            className="animate-slide-in rounded-xl border border-border/60 bg-white p-6 shadow-sm"
            style={{ animationDelay: '300ms' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-foreground">You vs. the market</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  A worked example of how your current settings would nudge a rate that&rsquo;s
                  sitting below the market. Illustrative figures &mdash; the live stage runs against
                  your real compset median.
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-fuchsia-100 px-2.5 py-0.5 text-[11px] font-semibold text-fuchsia-700">
                {PREVIEW_RATE_INDEX.toFixed(2)}&times; index
              </span>
            </div>

            {/* Position bar: your rate vs. compset median */}
            <div className="mt-5 space-y-2">
              <div className="relative h-2 w-full rounded-full bg-slate-100">
                {/* median marker at center */}
                <div className="absolute left-1/2 top-1/2 h-3.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-400" />
                {/* your-rate marker, positioned left of median (underpriced) */}
                <div className="absolute left-[22%] top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-fuchsia-500 shadow-sm" />
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium text-fuchsia-700">
                  Your rate {formatMoneySym(PREVIEW_BASE_RATE, sym)}
                </span>
                <span className="text-muted-foreground">
                  Compset median {formatMoneySym(PREVIEW_MEDIAN, sym)}
                </span>
              </div>
            </div>

            {/* Result of the current sensitivity/cap on this illustrative rate */}
            <div className="mt-5 flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
              {preview.disabled ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Ban className="h-4 w-4 shrink-0" />
                  <span>
                    Sensitivity is <strong className="text-foreground">Disabled</strong> &mdash; the
                    rate passes through unchanged at {formatMoneySym(PREVIEW_BASE_RATE, sym)}.
                  </span>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Market Position move
                    </p>
                    <p
                      className={cn(
                        'flex items-center gap-1 text-lg font-bold tabular-nums',
                        preview.finalPct > 0 && 'text-emerald-600',
                        preview.finalPct < 0 && 'text-rose-600',
                        preview.finalPct === 0 && 'text-foreground'
                      )}
                    >
                      {preview.finalPct > 0 && <TrendingUp className="h-4 w-4" />}
                      {preview.finalPct < 0 && <TrendingDown className="h-4 w-4" />}
                      {preview.finalPct === 0 && <Minus className="h-4 w-4" />}
                      {preview.finalPct > 0 ? '+' : ''}
                      {preview.finalPct}%
                    </p>
                    {preview.capHit && (
                      <p className="text-[10px] font-medium text-amber-600">
                        capped at &plusmn;{preview.effectiveCap}%
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-right">
                    <span className="text-base font-semibold tabular-nums text-muted-foreground">
                      {formatMoneySym(PREVIEW_BASE_RATE, sym)}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
                    <span className="text-lg font-bold tabular-nums text-foreground">
                      {formatMoneySym(preview.adjusted, sym)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!isDirty || saving} className="rounded-lg px-6">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
      {review.dialog}
    </OperatorLayout>
  )
}
